import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import { Calendar, MapPin, Clock, Ticket, ArrowRight, Users, Check } from "lucide-react";

function fmtDate(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function fmtTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function fmtDay(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { day: "", mon: "" };
  return {
    day: d.toLocaleDateString(undefined, { day: "2-digit" }),
    mon: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
  };
}

function RsvpForm({ event }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", seats: 1 });
  const [state, setState] = useState("idle"); // idle | submitting | done | error

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    try {
      await axios.post(`${API}/festivals/${event.id}/rsvp`, { ...form, seats: Number(form.seats) });
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="mt-4 bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] p-4 flex items-center gap-2 text-sm font-medium" data-testid={`rsvp-success-${event.id}`}>
        <Check className="w-4 h-4 shrink-0" /> You&apos;re on the list — we&apos;ll save you {form.seats} {Number(form.seats) === 1 ? "seat" : "seats"}. See you there!
      </div>
    );
  }

  return (
    <div className="mt-4">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`rsvp-toggle-${event.id}`}
          className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A5DFF]"
        >
          <Users className="w-4 h-4" /> Register interest
        </button>
      ) : (
        <form onSubmit={submit} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] p-4 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid={`rsvp-form-${event.id}`}>
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#0A0A0A]">Name</span>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid={`rsvp-name-${event.id}`} className="bg-white border-2 border-[#0A0A0A] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none" />
          </label>
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#0A0A0A]">Email</span>
            <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid={`rsvp-email-${event.id}`} className="bg-white border-2 border-[#0A0A0A] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#0A0A0A]">Seats</span>
            <select value={form.seats} onChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))} data-testid={`rsvp-seats-${event.id}`} className="bg-white border-2 border-[#0A0A0A] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none">
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="submit" disabled={state === "submitting"} data-testid={`rsvp-submit-${event.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] rounded-none px-5 py-2.5 text-sm font-medium whitespace-nowrap">
            {state === "submitting" ? "Sending…" : "Count me in"}
          </button>
          {state === "error" && <div className="text-[#FF2E63] text-xs font-bold" data-testid={`rsvp-error-${event.id}`}>Could not register — try again.</div>}
        </form>
      )}
    </div>
  );
}

function EventCard({ event, accent, upcoming }) {
  const { day, mon } = fmtDay(event.start_datetime);
  const startTime = fmtTime(event.start_datetime);
  const endTime = event.end_datetime ? fmtTime(event.end_datetime) : null;

  return (
    <article
      className="bg-white border-2 border-[#0A0A0A] pop-shadow p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6"
      data-testid={`festival-card-${event.id}`}
    >
      {/* Date block */}
      <div className="md:col-span-3 lg:col-span-2">
        <div className={`${accent} border-2 border-[#0A0A0A] p-4 inline-flex flex-col items-center min-w-[110px]`}>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0A0A0A]">{mon}</div>
          <div className="font-heading font-black text-[#0A0A0A] text-5xl leading-none mt-1">{day}</div>
        </div>
      </div>

      {/* Details */}
      <div className="md:col-span-6 lg:col-span-7">
        <div className="flex flex-wrap items-center gap-2 mb-2 text-xs uppercase tracking-[0.16em] font-bold text-[#1A5DFF]">
          <Calendar className="w-3.5 h-3.5" /> {fmtDate(event.start_datetime)}
          <span className="text-[#0A0A0A]">·</span>
          <Clock className="w-3.5 h-3.5" /> {startTime}{endTime && ` – ${endTime}`}
        </div>
        <h3 className="font-heading font-bold text-2xl text-[#0A0A0A] leading-tight">{event.title}</h3>
        <div className="mt-2 flex items-start gap-1.5 text-sm text-[#2C2C2C]">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FF2E63]" />
          <div>
            <span className="font-bold text-[#0A0A0A]">{event.venue}</span>
            <span className="text-[#2C2C2C]"> · {event.address} · {event.city}</span>
          </div>
        </div>
        <p className="mt-4 text-[#2C2C2C] leading-relaxed">{event.description}</p>
        {upcoming && <RsvpForm event={event} />}
      </div>

      {/* CTA */}
      <div className="md:col-span-3 flex md:justify-end md:items-center">
        {event.ticket_url ? (
          <a
            href={event.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`festival-tickets-${event.id}`}
            className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-3 text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Ticket className="w-4 h-4" /> Get tickets
          </a>
        ) : (
          <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#1A5DFF] border-2 border-[#1A5DFF] px-3 py-2 inline-flex items-center gap-1.5" data-testid={`festival-free-${event.id}`}>
            Free entry · drop in
          </div>
        )}
      </div>
    </article>
  );
}

const ACCENTS = ["bg-[#FFD93D]", "bg-[#FF2E63] text-[#FFF8E7]", "bg-[#1A5DFF] text-[#FFF8E7]", "bg-[#FFD93D]"];

export default function Festival() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/festivals`);
        setEvents(r.data || []);
      } catch {
        setError("Could not load festival dates.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = Date.now();
  const upcoming = useMemo(() => events.filter((e) => new Date(e.start_datetime).getTime() >= now), [events, now]);
  const past = useMemo(() => events.filter((e) => new Date(e.start_datetime).getTime() < now), [events, now]);

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-festival">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]" data-testid="festival-hero">
        <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Festival dates</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95]">
            Upcoming<br />
            <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">screenings & events.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            We tour curated programmes to independent cinemas across the UK. Every screening is low-sensory: house lights up, audio capped, captions on, fidget kits available.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="festival-host-cta"
              className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] pop-shadow rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Host a screening <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* UPCOMING */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-2">What&apos;s on</div>
              <h2 className="font-heading font-black text-[#0A0A0A] text-3xl sm:text-4xl tracking-tight">Upcoming</h2>
            </div>
            <div className="text-sm text-[#2C2C2C]" data-testid="festival-count">
              {upcoming.length} {upcoming.length === 1 ? "event" : "events"} announced
            </div>
          </div>

          {loading && <div className="text-[#2C2C2C]" data-testid="festival-loading">Loading dates…</div>}
          {error && <div className="text-[#FF2E63]" data-testid="festival-error">{error}</div>}
          {!loading && !error && upcoming.length === 0 && (
            <div className="bg-white border-2 border-[#0A0A0A] p-8 text-[#2C2C2C]" data-testid="festival-empty">
              No upcoming dates announced yet. Sign up to our membership or email <a href="mailto:create@divergentcinema.co.uk" className="underline text-[#0A0A0A] hover:text-[#FF2E63]">create@divergentcinema.co.uk</a> to get notified.
            </div>
          )}

          <div className="space-y-5" data-testid="festival-upcoming">
            {upcoming.map((e, i) => <EventCard key={e.id} event={e} accent={ACCENTS[i % ACCENTS.length]} upcoming />)}
          </div>
        </div>
      </section>

      {/* PAST */}
      {past.length > 0 && (
        <section className="py-12 md:py-16 bg-[#FFE8A8] border-t-2 border-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-2">Archive</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-3xl sm:text-4xl tracking-tight mb-8">Past screenings</h2>
            <div className="space-y-5 opacity-90" data-testid="festival-past">
              {past.map((e, i) => <EventCard key={e.id} event={e} accent={ACCENTS[i % ACCENTS.length]} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
